import { useMemo, useState } from 'react';
import { Button, Card, Group, Loader, Select, Stack, TextInput, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { AuditFilters } from '@/api/audit';
import { useAuditEvents } from '@/api/audit';
import { errorMessage } from '@/api/errors';
import { useEventTypes } from '@/api/ops';
import { EmptyState } from '@/components/EmptyState';
import { AuditTimeline } from '@/features/staff/AuditTimeline';

/** The whole audit trail with the filters staff actually reach for. */
export function AuditLog() {
  const { t } = useTranslation('ops');
  const [filters, setFilters] = useState<AuditFilters>({});
  const events = useAuditEvents(filters);
  const eventTypes = useEventTypes();

  // The select is grouped by event family, which is how staff think about the catalogue.
  const typeOptions = useMemo(() => {
    const families = new Map<string, string[]>();
    for (const row of eventTypes.data ?? []) families.set(row.family, [...(families.get(row.family) ?? []), row.key]);
    return [...families.entries()].map(([group, items]) => ({ group, items }));
  }, [eventTypes.data]);

  const set = (patch: AuditFilters) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <Stack gap="lg">
      <Title order={1}>{t('audit.title')}</Title>
      <Card>
        <Group align="flex-end" gap="md">
          <Select
            label={t('audit.type')}
            data={typeOptions}
            value={filters.type ?? null}
            onChange={(v) => set({ type: v ?? undefined })}
            searchable
            clearable
            w={260}
          />
          <TextInput label={t('audit.actor')} value={filters.actor ?? ''} onChange={(e) => set({ actor: e.currentTarget.value || undefined })} />
          <TextInput label={t('audit.project')} value={filters.project ?? ''} onChange={(e) => set({ project: e.currentTarget.value || undefined })} />
          <TextInput type="date" label={t('audit.since')} value={filters.since ?? ''} onChange={(e) => set({ since: e.currentTarget.value || undefined })} />
          <Button variant="default" onClick={() => setFilters({})}>
            {t('audit.reset')}
          </Button>
        </Group>
      </Card>
      {events.isPending ? <Loader color="forest" /> : events.isError ? <EmptyState title={errorMessage(events.error)} /> : <AuditTimeline events={events.data} />}
    </Stack>
  );
}
